import { FC, useState } from "react";
import { createPortal } from "react-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";

/** Antelacion minima para agendar o reagendar; el backend impone la misma. */
const RESPONSE_BUFFER_DAYS = 5;

type Props = {
    show: boolean;
    close: () => void;
    /** Fecha actualmente acordada, para mostrarla como referencia. */
    currentDate?: string | null;
    onSubmit: (proposedOn: string) => Promise<void> | void;
    loading?: boolean;
}

/**
 * Propone una nueva fecha para una solicitud.
 *
 * Deliberadamente minimo: un date picker y las dos acciones. Enviarla NO reagenda por si sola —
 * deja la propuesta a la espera de que la contraparte la confirme, y por eso el boton dice "Send"
 * y no "Reschedule".
 *
 * Va en un portal a document.body por la misma razon que CreateExpertiseModal: cualquier ancestro
 * con `transform` se vuelve el bloque contenedor de un `position: fixed` y el overlay se ajustaria
 * a el en vez de a la pantalla.
 */
export const RescheduleModal: FC<Props> = ({ show, close, currentDate, onSubmit, loading }) => {
    const [proposed, setProposed] = useState<string>('');
    const [error, setError] = useState<string>('');

    if (!show) return null;

    const submit = async () => {
        if (!proposed) {
            setError('Please choose a new date.');
            return;
        }
        setError('');
        await onSubmit(proposed);
    }

    return createPortal(
        <div className="nb-modal-overlay nb-modal-overlay--over-drawer" onClick={close}>
            <div className="nb-expertise-form nb-reschedule" onClick={(e) => e.stopPropagation()}>
                <button className="nb-expertise-form__close" onClick={close} type="button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#808080" strokeWidth="1" />
                        <path d="M9.17 9.17l5.66 5.66M14.83 9.17l-5.66 5.66" stroke="#808080" strokeWidth="1" />
                    </svg>
                </button>

                <div className="nb-expertise-form__title">
                    <h2>Request Reschedule</h2>
                    {currentDate && (
                        <span>ORIGINAL DATE · {dayjs(currentDate).format('DD / MM / YYYY - h:mma')}</span>
                    )}
                </div>

                <div className="nb-expertise-form__body">
                    <div className="form-field">
                        <label className="field-label">Proposed Date</label>
                        <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format="DD / MM / YYYY - HH:mm"
                            placeholder="DD / MM / YYYY"
                            value={proposed ? dayjs(proposed) : null}
                            // Mismo colchon de 5 dias que al crear la solicitud: mover la sesion
                            // a pasado manana dejaria a la otra parte sin margen para confirmarla,
                            // y el backend lo rechazaria igual.
                            disabledDate={(current) =>
                                current && current < dayjs().add(RESPONSE_BUFFER_DAYS, 'day').startOf('day')
                            }
                            popupStyle={{ zIndex: 10060 }}
                            onChange={(value) => {
                                setProposed(value ? value.toISOString() : '');
                                setError('');
                            }}
                        />
                        <span className="field-hint">
                            The other party will be asked to confirm this date.
                        </span>
                        {error && (
                            <div className="fv-plugins-message-container">
                                <div className="fv-help-block input-text-style fs-8">{error}</div>
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="button" className="cancel-btn" onClick={close} disabled={loading}>
                            Cancel
                        </button>
                        <button type="button" className="save-btn" onClick={submit} disabled={loading}>
                            {loading ? 'Sending...' : 'Send'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default RescheduleModal;
